// src/utils/albumFromPast.builder.js
// Heuristic-first album builder for "Create Album from Past Trip" flow.
// Uses AI scores if available but never blocks on them.

// Public API:
//   buildAlbumFromPast({ steps, results, vibes, likesOnly, layoutMode, selectionMode })
//     - steps: [{ id, date, title, location, address?, photos:[{ url, likes, dateTaken, coordinates?, width?, height? }] }]
//     - results: optional map url -> { scores:{people, scenery, activity, highlight}, hasPeople? }
//     - vibes:   Set<string> (e.g., new Set(['people','scenery','activity','highlight']))
//     - likesOnly: boolean
//     - layoutMode: 'elegant' | 'dense'
//     - selectionMode: 'diverse' | 'vibes' // NEW
//
// Returns: { pages: Page[] }
//
// Page types used by Flipbook/Editor:
//   - 'cover' { photos:[1] }
//   - 'stats' { meta:{ miles, days, steps, selectedPhotos, cities, countries, routeSvg } }
//   - 'route' { meta:{ svgDataUrl } }
//   - 'step-intro-left' { stepId, meta:{ title, date, place, insetMap, notePlaceholder }, photos:[1] }
//   - 'hero-right' { stepId, photos:[1] }
//   - 'grid-left'/'grid-right' { stepId, photos:[2-4], meta:{ align:'center' } }
//   - 'journal-left'/'journal-right' { meta:{ title } }

const DEBUG_VIBES = false;
const short = (u) => {
  if (!u) return '';
  if (u.startsWith('data:')) return 'data:' + u.slice(0, 30) + '…';
  const i = u.lastIndexOf('/');
  return i >= 0 ? u.slice(i + 1) : u;
};

// ============================================================
// URL to Data URL Conversion (for Capacitor WebView compatibility)
// ============================================================

/**
 * Fetches an image URL and converts it to a base64 data URL.
 * On iOS/Android with CapacitorHttp enabled, fetch() is automatically
 * patched to use native HTTP, bypassing WKWebView CORS restrictions.
 * @param {string} url - The URL to fetch
 * @returns {Promise<string>} - A data URL or the original URL if conversion fails
 */
async function fetchUrlAsDataUrl(url) {
  // Skip if already a data URL or empty
  if (!url || url.startsWith('data:')) {
    return url;
  }
  
  try {
    console.log('[fetchUrlAsDataUrl] Fetching:', url.substring(0, 80) + '...');
    
    // With CapacitorHttp enabled in config, fetch() is patched to use native HTTP
    // This bypasses WKWebView CORS restrictions on iOS
    const response = await fetch(url, {
      method: 'GET',
      // These headers help with some servers
      headers: {
        'Accept': 'image/*'
      }
    });
    
    if (!response.ok) {
      console.warn('[fetchUrlAsDataUrl] Fetch failed:', response.status, response.statusText);
      return url; // Return original URL as fallback
    }
    
    const blob = await response.blob();
    console.log('[fetchUrlAsDataUrl] Got blob, size:', blob.size, 'type:', blob.type);
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (result && result.startsWith('data:')) {
          console.log('[fetchUrlAsDataUrl] ✅ Converted to data URL, length:', result.length);
          resolve(result);
        } else {
          console.warn('[fetchUrlAsDataUrl] FileReader returned invalid result');
          resolve(url);
        }
      };
      reader.onerror = (err) => {
        console.warn('[fetchUrlAsDataUrl] FileReader error:', err);
        resolve(url); // Return original URL as fallback
      };
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn('[fetchUrlAsDataUrl] ❌ Error fetching URL:', err.message || err);
    return url; // Return original URL as fallback
  }
}

/**
 * Post-processes album pages to convert all Mapbox URLs to data URLs.
 * Call this after buildAlbumFromPast() before rendering.
 * @param {Array} pages - Array of album pages
 * @returns {Promise<Array>} - Pages with converted map URLs
 */
export async function convertAlbumMapsToDataUrls(pages) {
  if (!Array.isArray(pages)) return pages;
  
  console.log('[convertAlbumMapsToDataUrls] Processing', pages.length, 'pages...');
  
  const convertedPages = await Promise.all(pages.map(async (page) => {
    // Skip pages without meta
    if (!page.meta) return page;
    
    const newMeta = { ...page.meta };
    let changed = false;
    
    // Convert stepMapUrl (used in step-intro-left pages)
    if (newMeta.stepMapUrl && !newMeta.stepMapUrl.startsWith('data:')) {
      newMeta.stepMapUrl = await fetchUrlAsDataUrl(newMeta.stepMapUrl);
      changed = true;
    }
    
    // Convert svgDataUrl if it's a Mapbox URL (shouldn't be, but just in case)
    if (newMeta.svgDataUrl && newMeta.svgDataUrl.startsWith('https://api.mapbox.com')) {
      newMeta.svgDataUrl = await fetchUrlAsDataUrl(newMeta.svgDataUrl);
      changed = true;
    }
    
    // Convert globalMapSvgDataUrl if present
    if (newMeta.globalMapSvgDataUrl && newMeta.globalMapSvgDataUrl.startsWith('https://api.mapbox.com')) {
      newMeta.globalMapSvgDataUrl = await fetchUrlAsDataUrl(newMeta.globalMapSvgDataUrl);
      changed = true;
    }
    
    return changed ? { ...page, meta: newMeta } : page;
  }));
  
  console.log('[convertAlbumMapsToDataUrls] Done converting maps');
  return convertedPages;
}

// --- POLICY: liked vs deleted ---
function policyFilterPhotos(step, likesOnly = false) {
  const photos = Array.isArray(step.photos) ? step.photos : [];
  
  // Only filter to liked photos when likesOnly mode is explicitly enabled
  if (likesOnly) {
    const liked = photos.filter(p => !p?.deleted && (p?.likes || 0) > 0);
    if (liked.length) return liked;
  }
  
  // Otherwise, return all non-deleted photos
  return photos.filter(p => !p?.deleted);
}

// --- Helper from App.js for consistent location formatting ---
function formatOneLineLocation(step) {
  if (!step) return 'Unknown Location';

  // PRIORITY 1: If user manually edited the location field, use that
  if (step.location && step.location.trim() !== '') {
    return step.location.trim();
  }
  
  // PRIORITY 2: Try to get address from step level
  let addr = step.address || {};

  // PRIORITY 3: If step.address is missing location data, check photos
  const hasAnyLocation = 
    addr.city || addr.town || addr.locality || addr.village || addr.municipality ||
    addr.neighbourhood || addr.suburb || addr.quarter || addr.city_district ||
    addr.district || addr.state_district || addr.county || addr.region || addr.state;
  const hasStepAddr = (addr.country || addr.country_name) && hasAnyLocation;
  
  if (!hasStepAddr) {
    const photoWithAddr = (step.photos || []).find(p => {
      const pa = p.address;
      if (!pa) return false;
      const hasPhotoLocation = 
        pa.city || pa.town || pa.locality || pa.village || pa.municipality ||
        pa.neighbourhood || pa.suburb || pa.quarter || pa.city_district ||
        pa.district || pa.state_district || pa.county || pa.region || pa.state;
      return (pa.country || pa.country_name) && hasPhotoLocation;
    });
    if (photoWithAddr) {
      addr = photoWithAddr.address;
    }
  }

  // Format using the determined address
  const country = addr.country || addr.country_name || step.country || step.countryName || '';
  
  // Check city first, then region/state as fallback
  const city = addr.city || addr.town || addr.locality || addr.village || addr.municipality || '';
  const region = addr.neighbourhood || addr.suburb || addr.quarter || addr.city_district ||
    addr.district || addr.state_district || addr.county || addr.region || addr.state || '';
  
  const location = city || region;
    
  // Fallback if nothing found
  if (!country && !location) return 'Unknown Location';
  if (!location) return country;
  return `${country} · ${location}`;
}

// ------------------ helpers: trig + distance ------------------
// src/utils/albumFromPast.builder.js
// ... (lines 1-77 remain unchanged)

// ------------------ helpers: trig + distance ------------------
function makeStepMapSVG(step, width = 300, height = 180) {
  console.log('makeStepMapSVG called for step:', step?.title || step?.location);
  
  // Extract coordinates from step
  function extractCoordinatesFromStep(step) {
    if (Number.isFinite(step.lat) && Number.isFinite(step.lng)) {
      return { lat: step.lat, lng: step.lng };
    }
    const found = (step.photos || []).find(
      p => p.coordinates && Number.isFinite(p.coordinates.lat) && Number.isFinite(p.coordinates.lng)
    );
    return found ? found.coordinates : null;
  }
  
  const coords = extractCoordinatesFromStep(step);
  const MAPBOX_TOKEN = 'pk.eyJ1IjoibmdlcmdlcyIsImEiOiJjbWZsd29xbmUwOXBvMnFwdjkyZGF4NmVkIn0._En58wquwjmqCKIlXf4M7Q';

  // --- PATH 1: We have GPS coordinates (Highest Priority) ---
  if (coords) {
    console.log('Found GPS coordinates, generating coordinate-based map.');
    const { lat, lng } = coords;
    
    if (MAPBOX_TOKEN && MAPBOX_TOKEN !== 'pk.YOUR_MAPBOX_TOKEN_HERE') {
      // City-level zoom for individual steps
      const zoom = 12;
      const pin = `pin-s+3b82f6(${lng},${lat})`; // Blue pin
      const mapboxUrl = `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/static/${pin}/${lng},${lat},${zoom}/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`;
      
      console.log('Generated step map URL (from GPS):', mapboxUrl);
      return mapboxUrl;
    }
    
    // Fallback to simple SVG if no token but coords exist
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
      <defs>
        <pattern id="stepGrid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#ddd" stroke-width="1" opacity="0.5"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="#f5f5f5"/>
      <rect width="100%" height="100%" fill="url(#stepGrid)"/>
      <circle cx="50%" cy="50%" r="8" fill="#3b82f6" stroke="#fff" stroke-width="2"/>
      <text x="50%" y="85%" font-size="10" fill="#666" text-anchor="middle" font-family="system-ui">
        ${escapeXML(step?.location || step?.title || 'Location')}
      </text>
    </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  // --- PATH 2: No GPS, but we have a user-entered manual location (Second Priority) ---
  const userLocation = step?.location ? step.location.trim() : '';
  if (userLocation && userLocation.toLowerCase() !== 'home') { // Don't map "Home"
    console.log(`No GPS, but found user location: "${userLocation}". Generating string-based map.`);
    if (MAPBOX_TOKEN && MAPBOX_TOKEN !== 'pk.YOUR_MAPBOX_TOKEN_HERE') {
      const zoom = 12; // City-level zoom
      const encodedLocation = encodeURIComponent(userLocation);
      const pin = `pin-s+3b82f6(${encodedLocation})`; // Pin at the geocoded location
      
      // Use the string for both pin and center
      const mapboxUrl = `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/static/${pin}/auto/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`;
      
      console.log('Generated step map URL (from string):', mapboxUrl);
      return mapboxUrl;
    }
  }

  // --- PATH 3: No GPS and no user location (Fallback) ---
  console.log('No GPS coordinates or user location found for step, returning placeholder');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
    <rect width="100%" height="100%" fill="#eaeaea"/>
    <text x="50%" y="50%" fill="#999" font-family="system-ui" font-size="12" text-anchor="middle">No GPS data</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}


const R = (deg) => (deg * Math.PI) / 180;
function haversineMiles(a, b) {
  if (!a || !b) return 0;
  const dLat = R(b.lat - a.lat);
  const dLng = R(b.lng - a.lng);
  const la1 = R(a.lat);
  const la2 = R(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(la1) * Math.cos(la2);
  const c = 2 * Math.asin(Math.min(1, Math.sqrt(h)));
  return 3958.7613 * c; // earth radius miles
}

function toPointsFromSteps(steps) {
  const pts = [];
  for (const s of steps || []) {
    let coords = null;
    
    // First, check if step has direct coordinates (e.g., Home with geocoded location)
    if (Number.isFinite(s.lat) && Number.isFinite(s.lng)) {
      coords = { lat: s.lat, lng: s.lng };
    }
    
    // Then check photos for coordinates
    if (!coords) {
      const p = (s.photos || []).find(pp => pp && pp.coordinates);
      if (p && p.coordinates && Number.isFinite(p.coordinates.lat) && Number.isFinite(p.coordinates.lng)) {
        coords = { lat: p.coordinates.lat, lng: p.coordinates.lng };
      }
    }
    
    if (coords) {
      pts.push({ 
        lat: coords.lat, 
        lng: coords.lng, 
        label: s.address?.city || s.location || s.title || '',
        isHome: s.isHome || false
      });
    }
  }
  return pts;
}

function totalRouteMiles(pts) {
  if (!pts || pts.length < 2) return 0;
  
  let sum = 0;
  // Sum all forward legs
  for (let i = 1; i < pts.length; i++) {
    sum += haversineMiles(pts[i - 1], pts[i]);
  }
  
  // If the first point is Home, add the return leg (last point back to home)
  if (pts[0]?.isHome && pts.length >= 2) {
    sum += haversineMiles(pts[pts.length - 1], pts[0]);
  }
  
  return Math.round(sum);
}

function uniqCount(arr) {
  const s = new Set((arr || []).filter(Boolean));
  return s.size;
}
// === Album length algorithm (Part 1) ===
const ELEGANT_AVG_PHOTOS_PER_PAGE = 2.5;
const DENSE_AVG_PHOTOS_PER_PAGE   = 3;
const MIN_PAGES = 10;
const MAX_PAGES = 200;

function computeFinalPageCount({ totalSelectedPhotos, totalSteps, layoutMode }) {
  const avg = (layoutMode === 'dense') ? DENSE_AVG_PHOTOS_PER_PAGE : ELEGANT_AVG_PHOTOS_PER_PAGE;
  const photosForLayouts = Math.max(0, (totalSelectedPhotos || 0) - 1 /*cover*/ - (totalSteps || 0));
  const layoutPages = Math.ceil(photosForLayouts / Math.max(1, avg));
  let rawTotal = 3 /*cover+stats+route*/ + (totalSteps || 0) + layoutPages;
  let capped = Math.min(MAX_PAGES, Math.max(MIN_PAGES, rawTotal));
  if (capped % 2 !== 0) capped += 1;
  return capped;
}

function padToFinalPageCount(pages, target) {
  const out = Array.isArray(pages) ? pages.slice() : [];
  if (!Number.isFinite(target) || target <= out.length) return out;

  const need = target - out.length;
  for (let i = 0; i < need; i++) {
    const isLeft = out.length % 2 === 0; // even index => left page
    out.push({
      type: isLeft ? 'grid-left' : 'grid-right',
      templateKey: 'grid-2-tall',
      meta: { variant: 'grid-4-square', placeholder: true },
      photos: []
    });
  }

  // Safety: keep total even
  if (out.length % 2 !== 0) {
    out.push({
      type: 'grid-right',
      templateKey: 'grid-2-tall',
      meta: { variant: 'grid-4-square', placeholder: true },
      photos: []
    });
  }
  return out;
}



// === Pacing palettes (Part 2) ===
// We keep your existing page types ('step-intro-left', 'hero-right', 'grid-left/right'),
// and carry the intended visual as meta.variant. This keeps Flipbook working today,
// and lets you swap in new templates later with pg.templateKey if/when they exist.
const ELEGANT_LEFT_PALETTE = ['grid-4-square', 'collage-hero-left', 'collage-hero-left-tall', 'grid-2-tall'];
const DENSE_PALETTE        = ['grid-4-square', 'collage-hero-top-wide', 'grid-3-wide', 'collage-hero-left-tall'];

const TEMPLATE_CAPACITY = {
  'grid-4-square': 4,
  'collage-hero-left': 4, 
  'collage-hero-left-tall': 3,
  'grid-2-tall': 2,
  'grid-3-wide': 3,
  'collage-hero-top-wide': 3
};

const nextIdx = (i, arr) => (arr.length ? (i + 1) % arr.length : 0);
const capFor = (key) => TEMPLATE_CAPACITY[key] || 4;


// Capacities for template variants
const VARIANT_CAP = {
  'grid-4-square': 4,
  'collage-hero-left': 4, 
  'grid-3-wide': 3,
  'grid-2-tall': 2,
  'collage-hero-left-tall': 3,
  'collage-hero-top-wide': 3,
};

// Map a logical “variant” to a concrete Flipbook templateKey you already render
function keyForVariant(v) {
  switch (v) {
    case 'grid-4-square': return 'grid-2x2';     // 4-up square
    case 'grid-3-wide':   return 'grid-3-wide';  // 3-up wide
    case 'grid-2-tall':   return 'grid-2-tall';  // 2-up tall
    case 'collage-hero-left-tall': return 'collage-hero-left-tall';  // keep real collage keys
    case 'collage-hero-top-wide':  return 'collage-hero-top-wide';
    case 'collage-hero-left':      return 'collage-hero-left';       // add the 3rd collage
      
    default:
      return 'grid-2-tall';
  }
}

function capForVariant(v) {
  return VARIANT_CAP[v] || 2;
}



// Find the next variant in a palette that fits the remaining photos,
// optionally reserving 1 for a following hero (Elegant mode).
function nextVariantThatFits(
  palette,
  idx,
  remainingCount,
  { reserveForHero = false, disallow, disallowKeys } = {}
) {
  const needToKeep = reserveForHero ? 1 : 0;
  const usable = Math.max(0, remainingCount - needToKeep);
  if (usable <= 0) return { variant: null, idx };

  const disVar  = disallow ? new Set(disallow) : null;
  const disKeys = disallowKeys ? new Set(disallowKeys) : null;

  const choose = (avoid) => {
    let tries = 0;
    let i = idx;
    while (tries < palette.length) {
      const v = palette[i];
      const cap = capForVariant(v);
      const key = keyForVariant(v);             // <— check the rendered style too
      const blocked =
        avoid && (
          (disVar  && disVar.has(v)) ||
          (disKeys && disKeys.has(key))
        );
      if (cap <= usable && !blocked) {
        return { variant: v, idx: (i + 1) % palette.length };
      }
      i = (i + 1) % palette.length;
      tries++;
    }
    if (usable >= 2) return { variant: 'grid-2-tall', idx }; // safe fallback
    return { variant: null, idx };
  };

  // Pass 1: try to respect disallowed variants/keys
  const r1 = choose(true);
  if (r1.variant) return r1;

  // Pass 2: relax if we have to (prevents getting stuck)
  return choose(false);
}




// ------------------ stats + SVG builders ------------------

function computeTripStats(steps) {
  // days - parse as LOCAL time by appending T00:00:00
  const dates = steps.map(s => {
    const d = s.date || s.dateTaken;
    if (!d) return new Date();
    return new Date(d + 'T00:00:00');
  });
  const minD = new Date(Math.min(...dates));
  const maxD = new Date(Math.max(...dates));
  const days = Math.max(1, Math.round((maxD - minD) / 86400000) + 1);

  // distance (center of each step)
  const centers = steps
    .map(s => {
      const pts = s.photos?.map(p => p.coordinates).filter(Boolean) || [];
      if (!pts.length) return null;
      const avg = pts.reduce(
        (acc, c) => ({ lat: acc.lat + c.lat, lng: acc.lng + c.lng }),
        { lat: 0, lng: 0 }
      );
      return { lat: avg.lat / pts.length, lng: avg.lng / pts.length };
    })
    .filter(Boolean);

  const miles = centers.reduce((sum, _, i) => (i ? sum + haversineMiles(centers[i - 1], centers[i]) : sum), 0);

  // cities / countries
  const cities = new Set();
  const countries = new Set();
  steps.forEach(s => {
    s.photos?.forEach(p => {
      if (p.address?.city) cities.add(p.address.city);
      if (p.address?.country) countries.add(p.address.country);
    });
  });

  return {
    days,
    miles: Math.round(miles),
    stepsCount: steps.length,
    citiesCount: cities.size,
    countriesCount: countries.size,
    countriesList: [...countries],
  };
}

function makeRouteSVG(steps, w = 1200, h = 800) {
  const pts = toPointsFromSteps(steps);
  const pad = 50;
  if (pts.length < 2) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#0ea5e9"/><stop offset="100%" stop-color="#0f172a"/>
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#bg)"/>
      <text x="50%" y="50%" fill="#e2e8f0" font-family="system-ui,sans-serif" font-size="38" text-anchor="middle" dominant-baseline="middle">Route</text>
    </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  const lats = pts.map(p => p.lat), lngs = pts.map(p => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const toXY = ({ lat, lng }) => {
    const x = pad + ((lng - minLng) / (maxLng - minLng || 1)) * (w - pad * 2);
    const y = h - pad - ((lat - minLat) / (maxLat - minLat || 1)) * (h - pad * 2);
    return [x, y];
  };

  const coords = pts.map(toXY);
  const line = coords.map(([x, y]) => `${x},${y}`).join(' ');
  const markers = coords.map(([x, y], i) => {
    const fill = i === 0 ? '#22c55e' : (i === coords.length - 1 ? '#ef4444' : '#60a5fa');
    const label = escapeXML(pts[i].label || String(i + 1));
    return `<g><circle cx="${x}" cy="${y}" r="7" fill="${fill}" stroke="#0f172a" stroke-width="2"/>
      <text x="${x + 10}" y="${y - 10}" fill="#e2e8f0" font-size="18" font-family="system-ui,sans-serif">${label}</text></g>`;
  }).join('');

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#0ea5e9"/><stop offset="100%" stop-color="#0f172a"/>
      </linearGradient>
    </defs>
    <rect width="100%" height="100%" fill="url(#bg)"/>
    <polyline points="${line}" fill="none" stroke="#e2e8f0" stroke-width="3" opacity="0.9"/>
    ${markers}
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// Full-bleed route map: fits all step coords to the page, with light padding.
function makeCityMapSVG(steps, opts = {}) {
  const W = opts.width  || 2400;   // render big; your viewer will scale down
  const H = opts.height || 1600;
  const PAD = Math.min(0.12, Math.max(0, opts.paddingPct ?? 0.10)); // Increased from 0.06-0.08 to 0.10-0.12 for better margins

  // 1) Collect coordinates (include Home/Step 0 if it has coords)
  const pts = [];
  for (const s of (steps || [])) {
    const lat = s.lat ?? s.latitude ?? s.coords?.lat ?? s.location?.lat ?? s.address?.lat;
    const lon = s.lon ?? s.lng ?? s.longitude ?? s.coords?.lon ?? s.location?.lon ?? s.address?.lon;
    if (Number.isFinite(lat) && Number.isFinite(lon)) pts.push({ lat: +lat, lon: +lon });
  }
  if (!pts.length) {
    // Fallback: blank SVG so the page doesn’t crash
    const blank = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}"></svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(blank)}`;
  }

  // 2) Compute bounding box
  let minLat = +Infinity, maxLat = -Infinity, minLon = +Infinity, maxLon = -Infinity;
  for (const p of pts) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
    if (p.lon > maxLon) maxLon = p.lon;
  }

  // 3) Pad bbox (a bit), then fit to page aspect
  let dLat = Math.max(1e-6, maxLat - minLat);
  let dLon = Math.max(1e-6, maxLon - minLon);
  minLat -= dLat * PAD; maxLat += dLat * PAD;
  minLon -= dLon * PAD; maxLon += dLon * PAD;

  // Preserve aspect ratio
  const boxRatio = (maxLon - minLon) / (maxLat - minLat);
  const pageRatio = W / H;
  if (boxRatio > pageRatio) {
    // expand latitude span
    const target = (maxLon - minLon) / pageRatio;
    const extra = target - (maxLat - minLat);
    minLat -= extra / 2; maxLat += extra / 2;
  } else {
    // expand longitude span
    const target = (maxLat - minLat) * pageRatio;
    const extra = target - (maxLon - minLon);
    minLon -= extra / 2; maxLon += extra / 2;
  }

  // 4) Project lon/lat -> x/y (equirectangular; great for regional trips)
  const x = lon => ( (lon - minLon) / (maxLon - minLon) ) * W;
  const y = lat => ( 1 - (lat - minLat) / (maxLat - minLat) ) * H;

  const xy = pts.map(p => ({ x: x(p.lon), y: y(p.lat) }));

  // 5) Build SVG (no label/title; full-bleed)
  const route = xy.map((p,i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const circles = xy.map(p => `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="12" fill="#10b981" stroke="#ffffff" stroke-width="5" opacity="0.95"/>`).join('');

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">
  <rect x="0" y="0" width="${W}" height="${H}" fill="#0b1220"/>
  <path d="${route}" fill="none" stroke="#60a5fa" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" opacity="0.95"/>
  ${circles}
</svg>`.trim();

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}


function makeInsetSVG(step) {
  const title = step.location || step.address?.city || '—';
  const date = step.date || '';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="420" height="180">
    <rect width="100%" height="100%" fill="#0f172a"/>
    <circle cx="56" cy="90" r="10" fill="#60a5fa"/>
    <text x="84" y="84" fill="#e2e8f0" font-family="system-ui,sans-serif" font-size="20">${escapeXML(title)}</text>
    <text x="84" y="112" fill="#94a3b8" font-family="system-ui,sans-serif" font-size="16">${escapeXML(date)}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function escapeXML(s = '') {
  return String(s).replace(/[<>&'"]/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;',"'":'&apos;','"':'&quot;'}[c]));
}

// Build stats meta after we know how many photos made it in.
function buildStatsMetaFromSelected(steps, selectedPhotosCount) {
  // Calculate days as the date RANGE (last date - first date + 1), not count of unique dates
  // This gives accurate trip duration even when some days have no steps
  // For merged steps, we need to consider BOTH date (earliest) AND endDate (latest)
  const validDates = (steps || [])
    .flatMap(s => [s.date, s.dateTaken, s.startDate, s.endDate]) // Include all date fields
    .filter(d => d && d !== '0' && d !== 0);
  
  let days = 1; // Default to 1 day minimum
  if (validDates.length >= 1) {
    // Parse as LOCAL time by appending T00:00:00 (without Z = local, not UTC)
    const dateObjs = validDates
      .map(d => new Date(d + 'T00:00:00'))
      .filter(d => !isNaN(d) && d.getFullYear() > 1970);
    
    if (dateObjs.length >= 1) {
      const minD = new Date(Math.min(...dateObjs));
      const maxD = new Date(Math.max(...dateObjs));
      // Calculate the range in days (inclusive, so +1)
      days = Math.max(1, Math.round((maxD - minD) / 86400000) + 1);
    }
  }
  
  // Chapter count: exclude home, count merged cities as ONE
  const chapters = (steps || []).filter(s => {
    // Exclude home steps
    const location = (s.location || s.city || '').toLowerCase();
    if (location === 'home' || s.isHome) return false;
    return true;
  }).reduce((count, step) => {
    // If this step has mergedIds, it's a merged step - count it as 1
    // If this step's ID appears in another step's mergedIds, skip it (already counted)
    if (step.mergedIds && step.mergedIds.length > 1) {
      return count + 1; // This is the "parent" merged step
    }
    // Check if this step is part of someone else's merge
    const isPartOfMerge = (steps || []).some(other => 
      other.id !== step.id && 
      other.mergedIds && 
      other.mergedIds.includes(step.id)
    );
    if (isPartOfMerge) {
      return count; // Skip, already counted in parent
    }
    return count + 1; // Regular step
  }, 0);
  
  const cities = uniqCount((steps || []).map(s => s.address?.city || s.location));
  const countries = uniqCount((steps || []).map(s => s.address?.country));
  const miles = totalRouteMiles(toPointsFromSteps(steps)); // Miles include home

  return {
    miles,
    totalMiles: miles, // Add totalMiles for StatsCard compatibility
    days,
    steps: chapters, // Use chapter count instead of raw step count
    stepsLogged: chapters, // Add stepsLogged for StatsCard compatibility
    selectedPhotos: selectedPhotosCount,
    photos: selectedPhotosCount, // Add photos for StatsCard compatibility
    cities,
    countries,
    routeSvg: makeRouteSVG(steps, 1000, 700),
  };
}

// ------------------ scoring / ranking ------------------

const DEFAULT_VIBES = ['people', 'scenery', 'activity', 'highlight'];

// ==================== SIMILARITY DETECTION ====================
// Detects visually similar photos and applies scoring penalties to encourage variety.
// Uses "average hash" - very fast perceptual hashing that works on any image.

// Feature flag - set to false to disable similarity detection
const ENABLE_SIMILARITY_DETECTION = true;

const HASH_SIZE = 8; // 8x8 grid = 64-bit hash
const photoHashCache = new Map(); // Cache hashes to avoid recomputation

/**
 * Generate an 8x8 grayscale average hash for an image.
 * This is a perceptual hash that's resilient to minor edits/crops.
 * Works synchronously for data URLs (which are already in memory).
 */
function getAverageHash(dataUrl) {
  if (!ENABLE_SIMILARITY_DETECTION) return null;
  if (!dataUrl || typeof dataUrl !== 'string') return null;
  
  // Check cache first
  if (photoHashCache.has(dataUrl)) {
    return photoHashCache.get(dataUrl);
  }
  
  try {
    // Only process data URLs (blob URLs would require async)
    if (!dataUrl.startsWith('data:')) {
      return null;
    }
    
    // Create a small canvas to downsample the image
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    
    canvas.width = HASH_SIZE;
    canvas.height = HASH_SIZE;
    
    // Create and load image synchronously (works for data URLs)
    const img = new Image();
    
    // For data URLs, this is instant (no network request)
    img.src = dataUrl;
    
    // We need the image to be loaded before we can draw it
    // For data URLs in most browsers, this is synchronous, but we check
    if (img.complete && img.naturalWidth > 0) {
      ctx.drawImage(img, 0, 0, HASH_SIZE, HASH_SIZE);
    } else {
      // Image not ready, can't hash it synchronously
      return null;
    }
    
    const imageData = ctx.getImageData(0, 0, HASH_SIZE, HASH_SIZE);
    const pixels = imageData.data;
    const grayValues = [];
    
    let totalGray = 0;
    for (let i = 0; i < pixels.length; i += 4) {
      // Simple grayscale: average RGB
      const gray = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
      grayValues.push(gray);
      totalGray += gray;
    }
    
    const avgGray = totalGray / grayValues.length;
    
    // Build binary hash string
    let hash = '';
    for (const gray of grayValues) {
      hash += (gray > avgGray) ? '1' : '0';
    }
    
    // Cache it
    photoHashCache.set(dataUrl, hash);
    return hash;
  } catch (err) {
    // Silently fail - similarity detection is optional
    return null;
  }
}

/**
 * Calculate Hamming distance between two hashes (how many bits differ).
 * Lower distance = more similar photos.
 */
function getHammingDistance(hash1, hash2) {
  if (!hash1 || !hash2 || hash1.length !== hash2.length) return Infinity;
  
  let distance = 0;
  for (let i = 0; i < hash1.length; i++) {
    if (hash1[i] !== hash2[i]) distance++;
  }
  return distance;
}

/**
 * Calculate similarity penalty for a photo based on previously selected photos.
 * Returns a negative score to penalize similar photos.
 * 
 * Note: Higher threshold = more photos marked as similar = stricter on variety
 * 
 * @param {Object} photo - Photo to check
 * @param {Array} previousPhotos - Already selected photos to compare against
 * @param {Number} threshold - Hamming distance threshold (higher = more marked as similar)
 * @returns {Number} - Penalty score (0 to -10)
 */
function calculateSimilarityPenalty(photo, previousPhotos = [], threshold = 15) {
  if (!ENABLE_SIMILARITY_DETECTION) return 0;
  if (!photo || !photo.url || !previousPhotos || previousPhotos.length === 0) {
    return 0; // No penalty if nothing to compare
  }
  
  // Only apply to steps with many photos (where similarity is a real issue)
  if (previousPhotos.length < 5) {
    return 0; // Too few photos to worry about similarity
  }
  
  const hash = getAverageHash(photo.url);
  if (!hash) return 0; // Can't hash, no penalty
  
  let minDistance = Infinity;
  
  // Find the minimum distance to any previously selected photo
  for (const prev of previousPhotos) {
    if (!prev || !prev.url) continue;
    const prevHash = getAverageHash(prev.url);
    if (!prevHash) continue;
    
    const distance = getHammingDistance(hash, prevHash);
    minDistance = Math.min(minDistance, distance);
    
    // Early exit if we find a very similar photo
    if (distance < 5) break;
  }
  
  // Apply adaptive penalty based on distance
  if (minDistance <= 8) {
    return -10; // Very similar (likely burst shots) - strong penalty
  } else if (minDistance <= 12) {
    return -6; // Quite similar - moderate penalty
  } else if (minDistance <= threshold) {
    return -3; // Somewhat similar - light penalty
  }
  
  return 0; // Different enough - no penalty
}

// Adaptive threshold based on pool size
// Higher threshold = more photos marked as similar = stricter on variety
function getSimilarityThreshold(poolSize) {
  if (poolSize < 10) return Infinity;  // Don't apply similarity detection
  if (poolSize < 30) return 12;        // Permissive (allow some similarity)
  if (poolSize < 100) return 14;       // Moderate
  return 17;                            // Strict (want lots of variety in big collections)
}

// ==================== END SIMILARITY DETECTION ====================

// CHANGE #1: New "smarter" heuristic scoring for "Diverse" mode.
// It's fast because it only uses data already on the photo object.

function scorePhotoHeuristic(p, previousPhotos = []) {
  if (!p) return -1e9;
  let score = 0.2; // Baseline score to include photos with 0 likes.
  
  // 1. Boost for likes (strongest signal)
  score += (p.likes || 0) * 0.25;
  
  // 2. Boost for landscape aspect ratio (helps avoid screenshots)
  if (p.width && p.height && p.width > p.height) {
    score += 0.1;
  }
  
  // 3. NEW: Apply similarity penalty to encourage variety
  const threshold = getSimilarityThreshold(previousPhotos.length + 1);
  const similarityPenalty = calculateSimilarityPenalty(p, previousPhotos, threshold);
  score += similarityPenalty;
  
  return score;
}

function normalizeKey(u) {
  try {
    // Quick pass-through for data: URLs (no query to strip usually)
    if (typeof u === 'string' && u.startsWith('data:')) return u;

    // Use window.location.origin if available; never touch bare `location`
    const baseOrigin =
      (typeof window !== 'undefined' &&
       window.location &&
       window.location.origin) ? window.location.origin : 'http://x';

    const url = new URL(u, baseOrigin);
    url.search = ''; // strip query so hints match even with cache-busters
    return url.href;
  } catch {
    // Fallback: just strip query if URL ctor fails
    return String(u || '').replace(/\?.*$/, '');
  }
}



// Prefer-people signal from face hints (URL-keyed)
const DIVERSE_PEOPLE_WEIGHT = 1.7; // tune 1.2–1.8 for stronger/weaker bias
const COVER_PEOPLE_WEIGHT = 1.9; // stronger bias only for the cover
function personHintScore(p, peopleHints) {
  if (!p || !p.url || !peopleHints) return 0;
  const key = normalizeKey(p.url);
  const h = peopleHints[key] || peopleHints[p.url];
  if (!h || h.faceCount <= 0) return 0;
  // 0.6 base presence + scaled coverage (cap ~0.95)
  const s = Math.min(0.95, 0.6 + Math.min(0.35, (h.faceCoverage || 0) * 2));
  return s;
}


// The original ML-based scoring function, used for "Vibes" mode.
// --- FIND THIS ---
function scorePhoto(p, { results, vibes, likesOnly, previousPhotos = [] }) {
  if (!p) return -1e9;
  if (likesOnly && !(p.likes > 0)) return -1e9;

  const keys = (vibes && vibes.size ? Array.from(vibes) : DEFAULT_VIBES);
  const key = (p && p.url) ? normalizeKey(p.url) : null;
  const r = (results && key) ? (results[key] || results[p.url]) : null;
  const scores = r && (r.scores || r.score || r.vibeScores || r.clip);
  // keep r.scores alias in sync so downstream calls see it
  if (r && scores && !r.scores) r.scores = scores;
  // (optional) cache the alias so any other code that reads r.scores also works
if (r && scores && !r.scores) r.scores = scores;

  // Enhanced debugging for specific photos
  if (p.url && p.url.includes('.jpg')) { // Log all JPG photos
    console.log('Scoring photo:', {
      url: p.url.substring(p.url.lastIndexOf('/') + 1), // Just filename
      hasResults: !!results,
      foundInResults: !!r,
      vibes: keys,
      scores: r?.scores,
      likes: p.likes || 0
    });
  }

  if (scores) {
  let s = 0;
  for (const k of keys) {
    const vibeScore = Number((scores[k] ?? 0));
    s += vibeScore;
      if (vibeScore > 0.5) { // Log high vibe scores
        if (DEBUG_VIBES) {
  console.log(`High ${k} score (${vibeScore}) for:`, short(p.url));
}
      }
    }
    s += (p.likes || 0) * 0.05; // slight likes nudge
    
    // NEW: Apply similarity penalty to ML scores too
    const threshold = getSimilarityThreshold(previousPhotos.length + 1);
    const similarityPenalty = calculateSimilarityPenalty(p, previousPhotos, threshold);
    s += similarityPenalty * 0.1; // Scale penalty for ML scores (which are typically 0-4)
    
    return s;
  }

  // Fallback heuristic if ML scores are not available
  console.log('Using fallback heuristic for:', p.url.substring(p.url.lastIndexOf('/') + 1));
  return scorePhotoHeuristic(p, previousPhotos);
}

function rankPhotos(pool, opts) {
  return (pool || [])
    .map(p => ({ p, s: scorePhoto(p, opts) }))
    .filter(r => r.s > -1e8)
    .sort((a, b) => b.s - a.s)
    .map(r => r.p);
}

/**
 * NEW: Smart photo picker that selects photos incrementally,
 * applying similarity penalties as photos are selected.
 * This encourages variety without eliminating any candidates.
 * 
 * IMPORTANT: This does NOT affect face-first detection for covers/intros
 * because those are selected FIRST (when previousPhotos is empty) or
 * before 5+ photos have been selected (when penalty kicks in).
 */
function pickTopWithVariety(pool, k, opts) {
  if (!pool || pool.length === 0 || k <= 0) return [];
  if (k >= pool.length) {
    // If we need all photos, just rank normally
    return rankPhotos(pool, opts);
  }
  
  const selected = [];
  const remaining = [...pool];
  
  // Pick photos one by one, updating similarity context each time
  for (let i = 0; i < k && remaining.length > 0; i++) {
    // Score all remaining photos, considering what we've already picked
    const scored = remaining.map(p => ({
      p,
      s: scorePhoto(p, { ...opts, previousPhotos: selected })
    }));
    
    // Sort by score and pick the best
    scored.sort((a, b) => b.s - a.s);
    const best = scored[0].p;
    
    // Add to selected and remove from remaining
    selected.push(best);
    const idx = remaining.indexOf(best);
    if (idx !== -1) remaining.splice(idx, 1);
  }
  
  return selected;
}

// CHANGE #2: New function to pick a diverse spread of photos from a pre-ranked list.
// This ensures we get photos from the beginning, middle, and end of a step's timeline.
function evenSpreadPick(arr, k) {
  if (k >= arr.length) return [...arr];
  if (k === 0) return [];
  const out = [];
  const step = (arr.length - 1) / Math.max(1, k - 1);
  for (let i = 0; i < k; i++) {
    const idx = Math.round(i * step);
    out.push(arr[idx]);
  }
  return Array.from(new Set(out)); // Ensure uniqueness
}

function heroPick(ranked) {
  // Prefer landscape for full-bleed heroes to reduce cropping
  const land = ranked.find(p => (p && p.width && p.height) ? p.width >= p.height : true);
  return land || ranked[0];
}

// Prefer a face-free landscape for the ending/journal photo
// Prefer a face-free landscape for the ending/journal photo
function pickFaceFreeLandscape(photos = [], ctx) {
  if (!Array.isArray(photos) || photos.length === 0) return null;

  const faceFree = (p) => !hasPeopleHard(p, ctx);   // uses your existing helper
  const isLand   = (p) => isLandscape(p);          // uses your existing helper

  // Priority: landscape & face-free → any face-free → any landscape → first
  return (
    photos.find(p => isLand(p) && faceFree(p)) ||
    photos.find(p => faceFree(p)) ||
    photos.find(p => isLand(p)) ||
    photos[0] ||
    null
  );
}



function pickTop(pool, k, opts) {
  // Use variety-aware selection if we have enough photos and it would help
  const useVarietyPicker = pool && pool.length >= 10 && k >= 3 && k < pool.length;
  
  if (useVarietyPicker) {
    return pickTopWithVariety(pool, k, opts);
  }
  
  // Otherwise use standard ranking
  const ranked = rankPhotos(pool, opts);
  return ranked.slice(0, k);
}

function excludePhoto(arr, photo) {
  if (!photo) return arr || [];
  const pid = (photo.url || photo.id || photo.fileName);
  return (arr || []).filter(p => (p.url || p.id || p.fileName) !== pid);
}
// Prefer people-detected photos for the intro; fall back to strongest candidate
function hasPeople(p, peopleHints, results) {
  if (!p) return false;
  // 1) Face hints (worker URL-keyed)
  if (personHintScore(p, peopleHints) > 0) return true;

  // 2) Results map (ML scores, if present)
  const key = (p.url || '');
  const norm = normalizeKey(key);
  const r = (results && (results[norm] || results[key])) || null;
  if (r && (r.hasPeople || (r.scores && (r.scores.people || 0) > 0.5))) return true;

  // 3) Fallback flags, if any live on the photo
  if (p.hasPeople || (Array.isArray(p.faces) && p.faces.length > 0)) return true;

  return false;
}

function pickIntroPhoto(arr, { peopleHints, results }) {
  if (!arr || !arr.length) return null;
  const peopleFirst = arr.filter(p => hasPeople(p, peopleHints, results));
  if (peopleFirst.length) {
    // Bias toward “faces present” + our quick heuristic
    return peopleFirst
      .map(p => ({ p, s: scorePhotoHeuristic(p) + DIVERSE_PEOPLE_WEIGHT * personHintScore(p, peopleHints) }))
      .sort((a, b) => b.s - a.s)[0].p;
  }
  // Otherwise pick the standard hero candidate (prefers landscape)
  return heroPick(arr);
}

// Prefer no faces for 2-up Tall to avoid cropped faces
function peoplePresenceScore(p, { peopleHints, results }) {
  if (!p) return 0;
  const hinted = personHintScore ? (personHintScore(p, peopleHints) > 0) : false;
  const key = (p.url || '');
  const norm = (typeof normalizeKey === 'function') ? normalizeKey(key) : key;
  const r = (results && (results[norm] || results[key])) || null;
  const ml = (r && r.scores && Number(r.scores.people || 0)) || 0;
  const flags = (p.hasPeople || (Array.isArray(p.faces) && p.faces.length > 0)) ? 1 : 0;
  // 0 = no people … 1 = strong people presence
  return Math.max(ml, hinted ? 1 : 0, flags);
}

function pick2LeastPeople(arr, ctx) {
  if (!Array.isArray(arr) || arr.length <= 2) return { items: (arr || []).slice(0, 2), rest: (arr || []).slice(2) };
  const sorted = arr.slice().sort((a, b) => peoplePresenceScore(a, ctx) - peoplePresenceScore(b, ctx));
  const items = sorted.slice(0, 2);
  const chosen = new Set(items);
  const rest = arr.filter(p => !chosen.has(p));
  return { items, rest };
}

// Helper: Check if photo is panoramic (aspect ratio >= 2:1)
function isPanoramic(p) {
  if (!p) return false;
  const w = p.width || p.meta?.width || 0;
  const h = p.height || p.meta?.height || 0;
  if (!w || !h) return false;
  return (w / h) >= 2.0; // True panorama: 2:1 or wider
}

// Helper: Get panoramic score (higher = more panoramic)
function panoramicScore(p) {
  if (!p) return 0;
  const w = p.width || p.meta?.width || 0;
  const h = p.height || p.meta?.height || 0;
  if (!w || !h) return 0;
  const ratio = w / h;
  // Score: 0 for square/portrait, increasing for wider ratios
  // 1.5:1 = 0.5, 2:1 = 1.0, 3:1 = 2.0, etc.
  return Math.max(0, ratio - 1);
}

// Prefer 2 photos with absolutely no faces; if not enough, fall back to least-people
// ENHANCED: Now prioritizes panoramic photos for better 2-tall grid layouts
function pick2FaceFreeFirst(arr, ctx) {
  if (!Array.isArray(arr) || arr.length === 0) return { items: [], rest: [] };
  
  // 1) Filter for STRICTLY face-free photos (peoplePresenceScore must be 0)
  const faceFree = arr.filter(p => peoplePresenceScore(p, ctx) === 0);
  
  if (faceFree.length >= 2) {
    // 2) Among face-free photos, prefer panoramic ones
    const panoramas = faceFree.filter(p => isPanoramic(p));
    
    if (panoramas.length >= 2) {
      // Best case: We have 2+ panoramic face-free photos
      // Sort by panoramic score (widest first) and take top 2
      const sorted = panoramas
        .map(p => ({ p, score: panoramicScore(p) }))
        .sort((a, b) => b.score - a.score);
      
      const items = sorted.slice(0, 2).map(x => x.p);
      const chosen = new Set(items);
      const rest = arr.filter(p => !chosen.has(p));
      return { items, rest };
    }
    
    if (panoramas.length === 1) {
      // Mix: 1 panorama + 1 face-free landscape
      const landscapes = faceFree.filter(p => isLandscape(p) && !isPanoramic(p));
      if (landscapes.length > 0) {
        const items = [panoramas[0], landscapes[0]];
        const chosen = new Set(items);
        const rest = arr.filter(p => !chosen.has(p));
        return { items, rest };
      }
    }
    
    // Fallback: No panoramas, but we have face-free photos
    // Prefer landscapes over portraits
    const sorted = faceFree
      .map(p => ({ 
        p, 
        score: (isLandscape(p) ? 1 : 0) + panoramicScore(p) * 0.5 
      }))
      .sort((a, b) => b.score - a.score);
    
    const items = sorted.slice(0, 2).map(x => x.p);
    const chosen = new Set(items);
    const rest = arr.filter(p => !chosen.has(p));
    return { items, rest };
  }
  
  // 3) Fallback: < 2 face-free photos available
  // Try to use panoramas even if they have minimal faces (score < 0.3)
  const lowPeoplePanoramas = arr.filter(p => 
    isPanoramic(p) && peoplePresenceScore(p, ctx) < 0.3
  );
  
  if (lowPeoplePanoramas.length >= 2) {
    const items = lowPeoplePanoramas.slice(0, 2);
    const chosen = new Set(items);
    const rest = arr.filter(p => !chosen.has(p));
    return { items, rest };
  }
  
  // 4) Last resort: Use pick2LeastPeople (existing fallback)
  return pick2LeastPeople(arr, ctx);
}

// Prefer 3 face-free photos; otherwise pick the 3 lowest people scores.
function pick3FaceFreeFirst(arr, ctx) {
  const score = (p) => peoplePresenceScore(p, ctx);
  if (!Array.isArray(arr) || arr.length === 0) return { items: [], rest: [] };

  const faceFree = [];
  const faceSome = [];
  for (const p of arr) {
    (score(p) === 0 ? faceFree : faceSome).push(p);
  }

  if (faceFree.length >= 3) {
    const items = faceFree.slice(0, 3);
    const chosen = new Set(items);
    return { items, rest: arr.filter(p => !chosen.has(p)) };
  }

  // Fallback: take 3 with lowest people score
  const sorted = arr.slice().sort((a, b) => score(a) - score(b));
  const items = sorted.slice(0, Math.min(3, sorted.length));
  const chosen = new Set(items);
  return { items, rest: arr.filter(p => !chosen.has(p)) };
}

// If we can't avoid a face, put the "facey" one in the middle column (edges crop harsher).
function orderThreeFor3Wide(items, ctx) {
  if (!Array.isArray(items) || items.length !== 3) return items || [];
  const scored = items.map(p => ({ p, s: peoplePresenceScore(p, ctx) }))
                      .sort((a,b) => a.s - b.s); // low → high
  const [low, mid, high] = scored;
  return [low.p, high.p, mid.p];
}


// ---- choose a face-free landscape photo (fallback: any) ----
function isLandscape(p) {
  const w = p?.width || p?.meta?.width || 0;
  const h = p?.height || p?.meta?.height || 0;
  return w && h ? (w >= h) : false;
}
function hasPeopleHard(p, { peopleHints, results } = {}) {
  // re-use hints/results if available
  if (!p) return false;
  // 1) worker hints (URL-keyed)
  if (personHintScore && personHintScore(p, peopleHints) > 0) return true;
  // 2) results map
  const key = p.url || '';
  const norm = (typeof normalizeKey === 'function') ? normalizeKey(key) : key;
  const r = (results && (results[norm] || results[key])) || null;
  if (r && (r.hasPeople || (r.scores && (r.scores.people || 0) > 0.5))) return true;
  // 3) fallback flags
  return !!(p.hasPeople || (Array.isArray(p.faces) && p.faces.length > 0));
}



// Friendly date range for the cover
// Parse as LOCAL time by appending T00:00:00 (without Z = local, not UTC)
function safeDate(d) { 
  if (!d) return null;
  const t = new Date(d + 'T00:00:00'); 
  return isNaN(t) ? null : t; 
}
function firstStepDate(steps) {
  const ds = (steps || []).map(s => safeDate(s.date || s.dateTaken)).filter(Boolean);
  return ds.length ? new Date(Math.min(...ds)) : null;
}
function lastStepDate(steps) {
  const ds = (steps || []).map(s => safeDate(s.date || s.dateTaken)).filter(Boolean);
  return ds.length ? new Date(Math.max(...ds)) : null;
}
function fmtMonthDay(d) {
  return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
}
function fmtYear(d) {
  return d ? d.getFullYear() : '';
}
function fmtDateRange(start, end) {
  if (!start && !end) return '';
  if (start && end) {
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    const y = fmtYear(end);
    return sameMonth
      ? `${fmtMonthDay(start)}–${end.getDate()}, ${y}`
      : `${fmtMonthDay(start)} – ${fmtMonthDay(end)}, ${y}`;
  }
  const d = start || end;
  return `${fmtMonthDay(d)}, ${fmtYear(d)}`;
}


// ------------------ per-step block ------------------

function padToEven(pages) {
  const out = pages.slice();
  if (out.length % 2 !== 0) {
    out.push({
      type: 'grid-right',
      
      meta: { variant: 'grid-4-square', placeholder: true },
      photos: []
    });
  }
  return out;
}


function gridMetaCentered() {
  return { align: 'center' };
}

// CHANGE #3: The main block builder now accepts `selectionMode` to choose its logic.
function buildStepBlock(step, { layoutMode, likesOnly, results, vibes, selectionMode, peopleHints }) {
  // 0) Require at least 2 photos to include the step
  const pool = (step.photos || []).filter(p => (likesOnly ? p.likes > 0 : true));
  if (!pool || pool.length < 2) return [];

  // 1) Rank photos (keep your existing scoring)
  const k = Math.min(24, pool.length);
  let ranked;
  if (selectionMode === 'diverse') {
    const hr = pool
      .map(p => {
        const base = scorePhotoHeuristic(p);
        const peopleBias = DIVERSE_PEOPLE_WEIGHT * personHintScore(p, peopleHints);
        const r = results && (results[normalizeKey(p.url||'')] || results[p.url||'']);
        const mlBoost = (r && r.scores)
          ? (0.6*(r.scores.people||0) + 0.2*(r.scores.scenery||0) + 0.2*(r.scores.activity||0))
          : 0;
        return { p, s: base + peopleBias + mlBoost };
      })
      .sort((a,b)=>b.s-a.s)
      .map(it=>it.p);
    ranked = evenSpreadPick(hr, k);
  } else {
    ranked = rankPhotos(pool, { results, vibes, likesOnly }).slice(0, k);
  }

  if (ranked.length < 2) return [];

  // 2) Intro-left: people-first if possible
  const introPhoto =
    (selectionMode === 'diverse' && peopleHints)
      ? (ranked.find(p => personHintScore(p, peopleHints) > 0) || heroPick(ranked))
      : (typeof pickIntroPhoto === 'function'
          ? (pickIntroPhoto(ranked, { peopleHints, results }) || heroPick(ranked))
          : heroPick(ranked));

  let remaining = excludePhoto(ranked, introPhoto);
  // Use the consistent location formatter from App.js
  const bigTitle = formatOneLineLocation(step);
  
  // 🔍 MINIMAL DEBUG
  if (bigTitle === 'Unknown Location') {
    console.log('⚠️ Unknown Location for step:', {
      stepId: step.id,
      hasLocation: !!step.location,
      locationValue: step.location,
      hasStepAddress: !!step.address,
      stepAddressKeys: step.address ? Object.keys(step.address).join(', ') : 'none',
      photoCount: step.photos?.length || 0,
      firstPhotoHasAddress: !!step.photos?.[0]?.address
    });
  }
  
  // Get city/country for secondary fields (if any templates use them)
  const city = step.address?.city || '';
  const country = step.address?.country || '';

const pages = [{
  type: 'step-intro-left',
  stepId: step.id,
  meta: {
    title: bigTitle,               // City, Country in the big font
    date: step.date,
    place: step.location || city || '',
    city,
    country,
    hideStatsStrip: true,          // tell template to hide optional stats strip
    insetMap: makeInsetSVG(step),
    stepMapUrl: makeStepMapSVG(step, 300, 180),
    notePlaceholder: true
  },
  photos: [introPhoto]
}];


  // 3) Mode-specific pairs
  if (layoutMode === 'elegant') {
    // First pair closer: hero-right
    const firstHero = heroPick(remaining) || remaining[0];
    if (!firstHero) return []; // never emit empty second page
    pages.push({ type: 'hero-right', stepId: step.id, photos: [firstHero] });
    remaining = remaining.filter(p => p !== firstHero);

    // Add pairs: [left grid] + [hero-right] while we have enough photos
    let eIdx = 0;
    while (remaining.length >= 3) {
      // We need at least 2 for the left grid and 1 reserved for the following hero
      const { variant, idx } = nextVariantThatFits(ELEGANT_LEFT_PALETTE, eIdx, remaining.length, { reserveForHero: true });
      if (!variant) break;
      eIdx = idx;

      const cap = Math.min(capForVariant(variant), remaining.length - 1);
const k = keyForVariant(variant);
let leftItems, restAfterLeft;

if (k === 'grid-2-tall') {
  const picked = pick2FaceFreeFirst(remaining, { peopleHints, results });
  leftItems = picked.items;
  restAfterLeft = picked.rest;
  if (leftItems.length < 2 || restAfterLeft.length < 1) break; // enforce 2 tall + 1 hero
} else if (k === 'grid-3-wide') {
  const picked = pick3FaceFreeFirst(remaining, { peopleHints, results });
  leftItems = orderThreeFor3Wide(picked.items, { peopleHints, results });
  restAfterLeft = picked.rest;
  if (leftItems.length < 3 || restAfterLeft.length < 1) break;
} else {
  leftItems = remaining.slice(0, cap);
  restAfterLeft = remaining.slice(cap);
}

pages.push({
  type: 'grid-left',
  stepId: step.id,
  templateKey: k,
  photos: leftItems,
  meta: gridMetaCentered()
});
remaining = restAfterLeft;

      

      // Close the pair with a hero-right
      const hero = heroPick(remaining) || remaining[0];
      if (!hero) { pages.pop(); break; } // roll back left if we can't close the pair
      pages.push({ type: 'hero-right', stepId: step.id, photos: [hero] });
      remaining = remaining.filter(p => p !== hero);
    }

    // We stop when remaining < 3 to keep pairs even and avoid empties.
    return pages;
  }

  // DENSE: after intro-left, keep adding page pairs while photos remain
let dIdx = 0;
let lastVariant = null;
let lastKey = null; // <— track rendered style key

// Pair #1: close the spread with a right page
if (remaining.length === 1) {
  pages.push({ type: 'hero-right', stepId: step.id, photos: [remaining[0]] });
  return pages;
} else {
  const first = nextVariantThatFits(DENSE_PALETTE, dIdx, remaining.length, { reserveForHero: false });
  const vR0 = first.variant || 'grid-2-tall';
  dIdx = first.idx;
  const capR0 = Math.min(capForVariant(vR0), remaining.length);
const keyR0 = keyForVariant(vR0);
let rightItems0, restAfterR0;

if (keyR0 === 'grid-2-tall') {
  const picked = pick2FaceFreeFirst(remaining, { peopleHints, results });
  rightItems0 = picked.items;
  restAfterR0 = picked.rest;
} else if (keyR0 === 'grid-3-wide') {
  const picked = pick3FaceFreeFirst(remaining, { peopleHints, results });
  rightItems0 = orderThreeFor3Wide(picked.items, { peopleHints, results });
  restAfterR0 = picked.rest;
} else {
  rightItems0 = remaining.slice(0, capR0);
  restAfterR0 = remaining.slice(capR0);
}

pages.push({
  type: 'grid-right',
  stepId: step.id,
  templateKey: keyR0,
  photos: rightItems0,
  meta: gridMetaCentered()
});
remaining = restAfterR0;

  lastVariant = vR0;
  lastKey = keyR0;
}

// Additional pairs: [left grid] + [right grid/hero]
while (remaining.length >= 1) {
  const prevBeforeLVar = lastVariant;
  const prevBeforeLKey = lastKey;

  // LEFT: avoid same style as the page before it; keep 1 for right
  const pickL = nextVariantThatFits(
    DENSE_PALETTE,
    dIdx,
    remaining.length,
    {
      reserveForHero: true,
      disallow: prevBeforeLVar ? new Set([prevBeforeLVar]) : undefined,
      disallowKeys: prevBeforeLKey ? new Set([prevBeforeLKey]) : undefined
    }
  );
  if (!pickL.variant) break; // cannot form a left page while reserving for right

  const vL = pickL.variant;
  dIdx = pickL.idx;
  const capL = Math.min(capForVariant(vL), remaining.length - 1);
const keyL = keyForVariant(vL);
let leftItems, restAfterL;
if (keyL === 'grid-2-tall') {
  const picked = pick2FaceFreeFirst(remaining, { peopleHints, results });
  leftItems = picked.items;
  restAfterL = picked.rest;
  if (leftItems.length < 2 || restAfterL.length < 1) break;
} else if (keyL === 'grid-3-wide') {
  const picked = pick3FaceFreeFirst(remaining, { peopleHints, results });
  leftItems = orderThreeFor3Wide(picked.items, { peopleHints, results });
  restAfterL = picked.rest;
  if (leftItems.length < 3 || restAfterL.length < 1) break;
} else {
  leftItems = remaining.slice(0, capL);
  restAfterL = remaining.slice(capL);
}

pages.push({
  type: 'grid-left',
  stepId: step.id,
  templateKey: keyL,
  photos: leftItems,
  meta: gridMetaCentered()
});
remaining = restAfterL;

  lastVariant = vL;
  lastKey = keyL;

  // RIGHT: avoid matching the left *or* the page before left (prevents 3-in-a-row)
  if (remaining.length === 1) {
    pages.push({ type: 'hero-right', stepId: step.id, photos: [remaining[0]] });
    remaining = remaining.slice(1);
    break; // even pair complete
  } else {
    const disVar = new Set([vL]);
    const disKey = new Set([keyL]);
    if (prevBeforeLVar) disVar.add(prevBeforeLVar);
    if (prevBeforeLKey) disKey.add(prevBeforeLKey);

    const pickR = nextVariantThatFits(
      DENSE_PALETTE,
      dIdx,
      remaining.length,
      {
        reserveForHero: false,
        disallow: disVar,
        disallowKeys: disKey
      }
    );
    const vR = pickR.variant || 'grid-2-tall';
    dIdx = pickR.idx;
    const capR = Math.min(capForVariant(vR), remaining.length);
    const rightItems = remaining.slice(0, capR);
    const keyR = keyForVariant(vR);
    pages.push({
      type: 'grid-right',
      stepId: step.id,
      templateKey: keyR,
      photos: rightItems,
      meta: gridMetaCentered()
    });
    remaining = remaining.slice(capR);
    lastVariant = vR;
    lastKey = keyR;
  }
}

// If exactly one photo remains, close with a hero-right to keep the step even
if (remaining.length === 1) {
  pages.push({ type: 'hero-right', stepId: step.id, photos: [remaining[0]] });
}

// CRITICAL: Ensure each step has an EVEN number of pages
// This guarantees step-intro always lands on left page, next step-intro also on left
if (pages.length % 2 === 1) {
  // Odd number of pages - need to add one more page to make it even
  if (remaining.length > 0) {
    // Use remaining photos for the padding page
    const paddingPhotos = remaining.slice(0, Math.min(2, remaining.length));
    pages.push({
      type: 'grid-right',
      stepId: step.id,
      templateKey: paddingPhotos.length === 1 ? 'hero-right' : 'grid-2-tall',
      photos: paddingPhotos,
      meta: gridMetaCentered()
    });
  } else {
    // No remaining photos - use the intro photo again as a full-bleed
    // This creates a nice "bookend" effect for the chapter
    pages.push({
      type: 'hero-right',
      stepId: step.id,
      photos: [introPhoto],
      meta: { isPaddingPage: true } // Mark it so user can identify if needed
    });
  }
}

return pages;


  }




// ------------------ main builder ------------------


export function buildAlbumFromPast({
  steps = [],
  results = null,
  vibes = new Set(DEFAULT_VIBES),
  likesOnly = false,
  layoutMode = 'elegant',
  selectionMode = 'vibes',
  peopleHints = null,
  tripName = '',      // <— NEW
} = {}) {
  
  console.log('buildAlbumFromPast called with:', {
    steps: steps.length,
    hasResults: !!results,
    resultKeys: results ? Object.keys(results).length : 0,
    vibes: Array.from(vibes),
    selectionMode
  });

  // Enforce like/delete policy on every step before doing anything else
  steps = (steps || []).map(s => ({ ...s, photos: policyFilterPhotos(s, likesOnly) }));

  
  // 1) STEP-AWARE COVER SELECTION
  // Instead of picking globally, find the best photo from each step's top photos
  const stepTopPhotos = [];
  for (const step of steps) {
  const stepPool = (step.photos || []).filter(p => (likesOnly ? p.likes > 0 : true));
  if (stepPool.length > 0) {
    let stepTop;
    if (selectionMode === 'diverse' && peopleHints) {
      // Prefer a face-positive hero for this step
      const ranked = stepPool
        .map(p => {
          const base = scorePhotoHeuristic(p);
          const peopleBias = DIVERSE_PEOPLE_WEIGHT * personHintScore(p, peopleHints);
          return { p, score: base + peopleBias };
        })
        .sort((a, b) => b.score - a.score)
        .map(r => r.p);
      stepTop = ranked[0];
    } else {
      stepTop = pickTop(stepPool, 1, { results, vibes, likesOnly })[0];
    }
    if (stepTop) stepTopPhotos.push({ stepId: step.id, photo: stepTop });
  }
}

  
  // Now pick the best from step winners (not globally)
  let cover = null;
if (stepTopPhotos.length > 0) {
  if (selectionMode === 'diverse' && peopleHints) {
    // Prefer a face-positive cover across the step winners
    cover = stepTopPhotos
      .map(({ photo }) => {
        const base = scorePhotoHeuristic(photo);
        const peopleBias = DIVERSE_PEOPLE_WEIGHT * personHintScore(photo, peopleHints);
        return { p: photo, s: base + peopleBias };
      })
      .sort((a, b) => b.s - a.s)[0]?.p || stepTopPhotos[0].photo;
  } else {
    cover = pickTop(stepTopPhotos.map(i => i.photo), 1, { results, vibes, likesOnly })[0] || null;
  }
}


  console.log('Selected cover photo:', cover?.url, 'score:', cover ? scorePhoto(cover, { results, vibes, likesOnly }) : 'none');

  // 2) STEP-AWARE BLOCK BUILDING
  // Remove cover only from its source step, not from all steps
  const stepPages = [];
  for (const step of steps) {
    const coverFromThisStep = stepTopPhotos.find(item => 
      item.stepId === step.id && 
      item.photo === cover
    );
    
    const stepPhotos = coverFromThisStep 
      ? excludePhoto(step.photos || [], cover)  // Remove cover only from source step
      : step.photos || [];  // Leave other steps untouched
    
    const stepCopy = { ...step, photos: stepPhotos };
    const stepBlockPages = buildStepBlock(stepCopy, { layoutMode, likesOnly, results, vibes, selectionMode, peopleHints });
    
    console.log(`Step ${step.id} generated ${stepBlockPages.length} pages`);
    stepPages.push(...stepBlockPages);
  }

  // 3) Rest of the function stays the same...
  const usedIds = new Set();
  for (const pg of stepPages) {
    for (const p of (pg.photos || [])) {
      const id = p && (p.url || p.id || p.fileName);
      if (id) usedIds.add(id);
    }
  }
  if (cover) usedIds.add(cover.url || cover.id || cover.fileName);
  const selectedPhotosCount = usedIds.size;

  console.log('Total photos used in album:', selectedPhotosCount);

  // ... after you’ve built stepPages and computed selectedPhotosCount

const statsMeta = buildStatsMetaFromSelected(steps, selectedPhotosCount);

// Fixed opening: cover → stats → route (builder provides; UI may re-wrap with ensureIntroPages)
const pages = [];
if (cover) {
  // Calculate date range for cover (include startDate AND endDate for merged steps)
  const validDates = (steps || [])
    .flatMap(s => [s.date, s.dateTaken, s.startDate, s.endDate]) // Include all date fields
    .filter(d => d && d !== '0' && d !== 0); // Filter out null, undefined, 0, '0'
  
  // Parse as LOCAL time by appending T00:00:00 (without Z = local, not UTC)
  const ds = validDates
    .map(d => new Date(d + 'T00:00:00'))
    .filter(d => !isNaN(d) && d.getFullYear() > 1970); // Extra safety: valid dates after 1970
  
  const minD = ds.length ? new Date(Math.min(...ds)) : null;
  const maxD = ds.length ? new Date(Math.max(...ds)) : null;
  const dateRange =
    (minD && maxD) ? `${minD.toLocaleDateString()} — ${maxD.toLocaleDateString()}` : '';

  pages.push({
    type: 'cover',
    photos: [cover],
    meta: { title: (tripName || 'My Trip'), dateRange }
  });
}

pages.push({ type: 'stats', meta: statsMeta });

// ✅ NEW: use the same routeSvg that already looks at photo GPS,
// and mark this as a 'map' template so Flipbook renders it correctly.
const routeSvg = statsMeta.routeSvg || makeCityMapSVG(steps);

pages.push({ type: 'route', meta: { svgDataUrl: makeCityMapSVG(steps), hideTitle: true } });
pages.push(...stepPages);



// === Album length algorithm application ===
// We’ll leave space for the final two pages [timeline-left, journal-right]
// === Timeline (left) + Journal (right) — no placeholder padding ===

// === Timeline (left) + Journal (right) — no placeholder padding ===

// Reuse the "Export timeline" idea to build [{ when:'MM/DD/YYYY', places:[...] }]
function formatUSDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return `${String(m).padStart(2,'0')}/${String(d).padStart(2,'0')}/${y}`;
}
function cityCountry(step) {
  const addr = step?.address || {};
  const city =
    step.city || addr.city || addr.town || addr.village || addr.municipality || step.location || step.state || '';
  const country =
    step.country || addr.country || addr.country_name || (addr.country_code ? addr.country_code.toUpperCase() : '');
  const where = [city, country].filter(Boolean).join(', ');
  return where || (step.title || 'Unknown');
}

// Build timeline only from steps that actually produced album pages
const byDate = new Map();
for (const s of steps) {
  const produced = stepPages.some(pg => pg.stepId === s.id);
  if (!produced) continue;
  const d = s.date || s.dateTaken || '';
  const place = cityCountry(s);
  if (!byDate.has(d)) byDate.set(d, new Set());
  byDate.get(d).add(place);
}

const timelineDays = Array
  .from(byDate.entries())
  .sort((a,b) => String(a[0]).localeCompare(String(b[0])))
  .map(([d, places]) => ({ when: formatUSDate(d), places: Array.from(places) }));


// REMOVED: Filler page logic that was causing photos from wrong steps to appear
// Each step now guarantees even page count, so step-intros always land on left pages
// Timeline will naturally land on the correct page

pages.push({
  type: 'timeline',
  templateKey: 'timeline',
  photos: [],
  meta: { days: timelineDays, locked: true }
});


// Ending photo: prefer face-free landscape from the whole trip
const allTripPhotos = steps.flatMap(s => s.photos || []);
let endPhoto = pickFaceFreeLandscape(allTripPhotos, { peopleHints, results });

// If no suitable photo found, use the last photo from the trip
if (!endPhoto && allTripPhotos.length > 0) {
  endPhoto = allTripPhotos[allTripPhotos.length - 1];
}

// ALWAYS add journal-short as the final page
pages.push({ 
  type: 'journal-short',
  templateKey: 'journal-short',
  photos: endPhoto ? [endPhoto] : [], 
  meta: { locked: true } 
});

// Return the album as-is (no extra padding)
return { pages: pages };



// 2) build timeline items: MM/DD → City, Country



}

// Convenience wrapper for fast "Daily Digest / Current Trip" albums.
// Uses the same Diverse selection logic as buildAlbumFromPast but
// enforces an elegant, compact 5–15 page album and attaches an
// optional journal page at the end.
export function buildDigestAlbum({
  steps = [],
  results = null,
  likesOnly = false,
  tripName = '',
  peopleHints = null,
  minPages = 5,
  maxPages = 10,     //that how I can change maximum page number
  journalText = '',
} = {}) {
  // 1) Build a normal Diverse / Elegant album first
  const base = buildAlbumFromPast({
    steps,
    results,
    likesOnly,
    layoutMode: 'elegant',
    selectionMode: 'diverse',
    peopleHints,
    tripName,
  });

  let pages = Array.isArray(base?.pages) ? [...base.pages] : [];

  // 🔹 Daily Digest: remove the timeline page (Current Trip only)
  // We still want timeline for normal "Past Trip" albums, but not for
  // the compact Daily Digest / Current Trip flow.
  pages = pages.filter(
    (pg) => pg?.type !== 'timeline' && pg?.templateKey !== 'timeline'
  );

  // 2) Remove the default final journal-short page that the
  //    main builder always appends. For Daily Digest we only
  //    want a journal page if the user actually wrote one.
  if (pages.length && pages[pages.length - 1]?.type === 'journal-short') {
    pages.pop();
  }

  // 3) Clamp to a reasonable max page count, but preserve the cover.
  const safeMax = Math.max(1, maxPages | 0);
  if (pages.length > safeMax) {
    const cover = pages[0];
    const rest = pages.slice(1, safeMax);
    pages = [cover, ...rest];
  }

  // (Optional) we *could* try to pad up to minPages here, but in
  // practice it's better to just return a shorter book than to
  // duplicate pages. So minPages is a soft target, not enforced.

  // 4) Attach a journal page at the end *only* if text was provided.
  const jt = (journalText || '').trim();
  if (jt) {
    // Use the last page's first photo as the side image, if any.
    const lastPage = pages[pages.length - 1];
    const sidePhoto = lastPage?.photos?.[0] || null;

    pages.push({
      type: 'journal-short',
      templateKey: 'journal-short',
      photos: sidePhoto ? [sidePhoto] : [],
      meta: {
        locked: true,
        journalText: jt,
        isDigestJournal: true, // so the UI can treat it specially if desired
      },
    });
  }

  return { pages };
}

// Lightweight helper for Daily Digest / Current Trip flow.
// - Always uses 'diverse' selection (no Smart Vibes / CLIP).
// - Always uses 'elegant' layout.
// - Assumes steps are already filtered to the chosen date range.



export default buildAlbumFromPast;